import * as v from "valibot";

import type {
  InteractiveNotificationMessage,
  ScheduleNotificationV2Message,
} from "@textclub/common/bridge";
import {
  INTERACTIVE_OPTION_KEY_MAX,
  MAX_INTERACTIVE_MESSAGES,
  MAX_INTERACTIVE_OPTIONS_PER_MESSAGE,
  MAX_NOTIFICATION_SCHEDULE_DAYS,
  NOTIFICATION_BODY_CHAR_LIMIT,
  NOTIFICATION_CTA_CHAR_LIMIT,
  NOTIFICATION_ASSET_URL_MAX_LENGTH,
  NOTIFICATION_ASSET_URL_PATTERN,
  NOTIFICATION_INLINE_ASSET_MAX_BASE64_LENGTH,
  NOTIFICATION_INLINE_ASSET_MIME_PATTERN,
  NOTIFICATION_TITLE_CHAR_LIMIT,
} from "@textclub/common/constants";

import { instrumentSdkMethod } from "../instrumentation";
import { Record, parseArgs } from "../schema-utils";
import type { ModuleContext } from "./module-context";

const NotificationPriority = v.picklist(["low", "medium", "high", "critical"]);

const ScheduledAtField = v.pipe(
  v.date(),
  v.check(
    (d) =>
      d.getTime() <=
      Date.now() + MAX_NOTIFICATION_SCHEDULE_DAYS * 24 * 60 * 60 * 1000,
    `scheduledAt must be within ${MAX_NOTIFICATION_SCHEDULE_DAYS} days`,
  ),
);

const ScheduledInDaysField = v.pipe(
  v.number(),
  v.check((n) => n >= 0, "scheduledInDays cannot be negative"),
  v.maxValue(
    MAX_NOTIFICATION_SCHEDULE_DAYS,
    `scheduledInDays must be at most ${MAX_NOTIFICATION_SCHEDULE_DAYS}`,
  ),
);

const InlineAssetField = v.pipe(
  v.string(),
  v.regex(
    NOTIFICATION_INLINE_ASSET_MIME_PATTERN,
    "asset must be a base64 data URL with MIME image/png, image/jpeg, or image/webp",
  ),
  v.maxLength(
    NOTIFICATION_INLINE_ASSET_MAX_BASE64_LENGTH,
    "asset must be at most 2MB",
  ),
);

const RemoteAssetField = v.pipe(
  v.string(),
  v.regex(NOTIFICATION_ASSET_URL_PATTERN, "asset URL must start with https://"),
  v.maxLength(
    NOTIFICATION_ASSET_URL_MAX_LENGTH,
    `asset URL must be at most ${NOTIFICATION_ASSET_URL_MAX_LENGTH} characters`,
  ),
);

const BaseScheduleNotification = {
  assetReference: v.optional(v.string()),
  imageReference: v.optional(v.string()),
  asset: v.optional(
    v.union(
      [InlineAssetField, RemoteAssetField],
      "asset must be a png, jpeg, or webp data URL or an https:// URL",
    ),
  ),
  body: v.pipe(
    v.string(),
    v.nonEmpty("body cannot be empty"),
    v.maxLength(
      NOTIFICATION_BODY_CHAR_LIMIT,
      `body must be at most ${NOTIFICATION_BODY_CHAR_LIMIT} chars`,
    ),
  ),
  title: v.optional(
    v.pipe(
      v.string(),
      v.maxLength(
        NOTIFICATION_TITLE_CHAR_LIMIT,
        `title must be at most ${NOTIFICATION_TITLE_CHAR_LIMIT} chars`,
      ),
    ),
  ),
  ctaText: v.pipe(
    v.string(),
    v.nonEmpty("ctaText cannot be empty"),
    v.maxLength(
      NOTIFICATION_CTA_CHAR_LIMIT,
      `ctaText must be at most ${NOTIFICATION_CTA_CHAR_LIMIT} chars`,
    ),
  ),
  priority: v.optional(NotificationPriority),
  identifier: v.pipe(v.string(), v.nonEmpty("identifier is required")),
  entryPayload: v.optional(Record),
};

const ScheduleNotificationOpts = v.pipe(
  v.union([
    v.object({ ...BaseScheduleNotification, scheduledAt: ScheduledAtField }),
    v.object({
      ...BaseScheduleNotification,
      scheduledInDays: ScheduledInDaysField,
    }),
  ]),
  v.check(
    (opts) => !(opts.asset && (opts.assetReference || opts.imageReference)),
    "asset and assetReference are mutually exclusive",
  ),
);

const InteractiveOptionKey = v.pipe(
  v.string(),
  v.nonEmpty("option key cannot be empty"),
  v.maxLength(INTERACTIVE_OPTION_KEY_MAX),
  v.regex(/^[A-Za-z0-9_-]+$/, "keys may only contain letters, digits, - and _"),
);

const InteractiveOption = v.object({
  key: InteractiveOptionKey,
  label: v.pipe(
    v.string(),
    v.nonEmpty("option label cannot be empty"),
    v.maxLength(25, "option label must be at most 25 chars"),
  ),
});

const InteractiveMessageBody = v.pipe(
  v.string(),
  v.nonEmpty("message body cannot be empty"),
  v.maxLength(NOTIFICATION_BODY_CHAR_LIMIT),
);

const InteractiveStepMessage = v.strictObject({
  key: InteractiveOptionKey,
  body: InteractiveMessageBody,
  options: v.pipe(
    v.array(InteractiveOption),
    v.minLength(1, "a step message needs at least one option"),
    v.maxLength(MAX_INTERACTIVE_OPTIONS_PER_MESSAGE),
  ),
});

const InteractiveTerminalMessage = v.strictObject({
  key: InteractiveOptionKey,
  body: InteractiveMessageBody,
  ctaText: v.pipe(
    v.string(),
    v.nonEmpty("ctaText cannot be empty"),
    v.maxLength(NOTIFICATION_CTA_CHAR_LIMIT),
  ),
});

const InteractiveMessage = v.union([
  InteractiveStepMessage,
  InteractiveTerminalMessage,
]);

const BaseScheduleInteractive = {
  assetReference: v.optional(v.string()),
  priority: v.optional(NotificationPriority),
  identifier: v.optional(v.string()),
  entryPayload: v.optional(Record),
  messages: v.pipe(
    v.array(InteractiveMessage),
    v.minLength(1, "at least one message is required"),
    v.maxLength(MAX_INTERACTIVE_MESSAGES),
  ),
};

const ScheduleInteractiveOpts = v.union([
  v.object({ ...BaseScheduleInteractive, scheduledAt: ScheduledAtField }),
  v.object({
    ...BaseScheduleInteractive,
    scheduledInDays: ScheduledInDaysField,
  }),
]);

const UnscheduleNotificationOpts = v.object({
  identifier: v.string(),
});

/**
 * The NotificationsModule manages all v2 notification scheduling operations.
 */
export class NotificationsModule {
  /** Experimental, unstable notification APIs. */
  readonly experimental: ExperimentalNotificationsModule;

  constructor(private ctx: ModuleContext) {
    this.experimental = new ExperimentalNotificationsModule(ctx);
  }

  // Exposed module interface
  @instrumentSdkMethod({
    name: "notifications.scheduleNotification",
    redact: ["asset"],
  })
  scheduleNotification(
    options: {
      body: string;
      title?: string;
      ctaText: string;
      priority?: ScheduleNotificationV2Message["priority"];
      identifier?: string;
      entryPayload?: Record<string, unknown>;
    } & (
      | { scheduledAt: Date; scheduledInDays?: never }
      | { scheduledAt?: never; scheduledInDays: number }
    ) &
      (
        | {
            assetReference?: string;
            /** @deprecated Use assetReference instead */
            imageReference?: string;
            asset?: never;
          }
        | {
            assetReference?: never;
            imageReference?: never;
            /** A png, jpeg, or webp data URL (2 MiB encoded) or an https:// URL to one. */
            asset: string;
          }
      ),
  ): void {
    const parsed = parseArgs(ScheduleNotificationOpts, options);

    this.ctx.assertInitialized();

    const assetRef = parsed.assetReference ?? parsed.imageReference;
    const baseMessage = {
      type: "ScheduleNotificationV2" as const,
      ...(typeof assetRef === "string" ? { assetReference: assetRef } : {}),
      ...(parsed.asset !== undefined ? { asset: parsed.asset } : {}),
      body: parsed.body,
      title: parsed.title,
      ctaText: parsed.ctaText,
      priority: parsed.priority || "medium",
      identifier: parsed.identifier,
      entryPayload: parsed.entryPayload,
    };

    if ("scheduledInDays" in parsed) {
      this.ctx.sendMessage({
        ...baseMessage,
        scheduledInDays: parsed.scheduledInDays,
      });
    } else {
      this.ctx.sendMessage({
        ...baseMessage,
        scheduledAt: parsed.scheduledAt.toISOString(),
      });
    }
  }

  @instrumentSdkMethod({ name: "notifications.unscheduleNotification" })
  unscheduleNotification(options: { identifier: string }): void {
    parseArgs(UnscheduleNotificationOpts, options);
    this.ctx.assertInitialized();

    this.ctx.sendMessage({
      type: "UnscheduleNotification",
      identifier: options.identifier,
    });
  }

  /** Exposed for decorator use - delegates to context */
  instrumentMethod<T>(opts: { method: string; args: unknown; fn: () => T }): T {
    return this.ctx.instrumentMethod(opts);
  }
}

/**
 * Experimental notification APIs, exposed under `notifications.experimental`.
 * These are unstable and may change without a major version bump.
 */
class ExperimentalNotificationsModule {
  constructor(private ctx: ModuleContext) {}

  @instrumentSdkMethod({
    name: "notifications.experimental.scheduleInteractiveNotification",
  })
  scheduleInteractiveNotification(
    options: {
      assetReference?: string;
      priority?: ScheduleNotificationV2Message["priority"];
      identifier?: string;
      entryPayload?: Record<string, unknown>;
      messages: InteractiveNotificationMessage[];
    } & (
      | { scheduledAt: Date; scheduledInDays?: never }
      | { scheduledAt?: never; scheduledInDays: number }
    ),
  ): void {
    const parsed = parseArgs(ScheduleInteractiveOpts, options);

    this.ctx.assertInitialized();

    const baseMessage = {
      type: "ScheduleInteractiveNotification" as const,
      ...(typeof parsed.assetReference === "string"
        ? { assetReference: parsed.assetReference }
        : {}),
      priority: parsed.priority || "medium",
      identifier: parsed.identifier,
      entryPayload: parsed.entryPayload,
      messages: parsed.messages,
    };

    if ("scheduledInDays" in parsed) {
      this.ctx.sendMessage({
        ...baseMessage,
        scheduledInDays: parsed.scheduledInDays,
      });
    } else {
      this.ctx.sendMessage({
        ...baseMessage,
        scheduledAt: parsed.scheduledAt.toISOString(),
      });
    }
  }

  /** Exposed for decorator use - delegates to context */
  instrumentMethod<T>(opts: { method: string; args: unknown; fn: () => T }): T {
    return this.ctx.instrumentMethod(opts);
  }
}
